import { GENERIC_COUNTER_KEY, cardHasPrintedPT, getInstancePTDisplay } from '@mtg-deck-tool/shared';
import type { CardInstance } from '@mtg-deck-tool/shared';
import { usePlaytesterStore } from '../../stores/playtester-store.js';

interface CardBadgesProps {
  instance: CardInstance;
}

/**
 * Badge height, derived from the card width so badges scale with zoom.
 * Defined once: the badges size themselves with it AND the named-counter stack
 * offsets itself by it, so the two cannot drift apart.
 */
const BADGE_SIZE = 'calc(var(--pt-card-w) / 5)';

/**
 * Renders the power/toughness badge (bottom-right) and the generic counter badge
 * (bottom-left) for a card on the battlefield.
 *
 * Clicking either badge increments the respective value and stops propagation so
 * the card's own tap/select/drag handlers are not triggered.
 *
 * Used by both BattlefieldCard (player zone) and HordeSingleCard (horde zone) so
 * the two never drift apart.
 */
export default function CardBadges({ instance }: CardBadgesProps) {
  const adjustPowerToughness = usePlaytesterStore((s) => s.adjustPowerToughness);
  const addGenericCounter = usePlaytesterStore((s) => s.addGenericCounter);

  const { card, faceDown } = instance;

  // ── Generic counter badge (bottom-left) ─────────────────────────────────────
  const genericCount = instance.counters[GENERIC_COUNTER_KEY] ?? 0;

  // ── Named counter summary (secondary, bottom-left above generic) ─────────────
  const namedCounters = Object.entries(instance.counters).filter(
    ([key]) => key !== GENERIC_COUNTER_KEY,
  );

  // ── Power / toughness badge (bottom-right) ──────────────────────────────────
  // Show the badge only when the card has a printed P/T box or an override is set.
  const hasPT = !faceDown ? cardHasPrintedPT(card) : (instance.power !== undefined || instance.toughness !== undefined);

  // ── Token label ─────────────────────────────────────────────────────────────
  // Marks a card as a token so it is distinguishable at a glance — it matters
  // because player-created tokens cease to exist when they leave the
  // battlefield. Covers both player-created instances (isToken) and genuine
  // token CARDS, which is how the horde's own tokens arrive.
  const isTokenCard = !faceDown && (instance.isToken || card.layout === 'token');
  const ptDisplay = getInstancePTDisplay(card, instance);

  // Stop propagation on all badge clicks so the card's tap/select handler is not invoked.
  function handleCounterClick(e: React.MouseEvent) {
    e.stopPropagation();
    addGenericCounter(instance.instanceId);
  }

  function handlePTClick(e: React.MouseEvent) {
    e.stopPropagation();
    adjustPowerToughness(instance.instanceId, 1, 1);
  }

  return (
    <>
      {/* Named counter summary (small, secondary) — excludes the generic counter */}
      {namedCounters.length > 0 && (
        <div
          className="absolute left-0.5 flex flex-col gap-0.5 pointer-events-none"
          style={{ bottom: genericCount > 0 ? `calc(${BADGE_SIZE} + 4px)` : '0.125rem' }}
        >
          {namedCounters.slice(0, 2).map(([name, value]) => (
            <div
              key={name}
              className="bg-black/80 text-white font-bold px-1 py-0.5 rounded leading-none"
              style={{ fontSize: 'calc(var(--pt-card-w) / 14)' }}
            >
              {name}: {value}
            </div>
          ))}
          {namedCounters.length > 2 && (
            <div
              className="bg-black/80 text-gray-400 px-1 py-0.5 rounded leading-none"
              style={{ fontSize: 'calc(var(--pt-card-w) / 14)' }}
            >
              +{namedCounters.length - 2}
            </div>
          )}
        </div>
      )}

      {/* Token label — centred over the type line, like Moxfield's */}
      {isTokenCard && (
        <div
          className="absolute left-1/2 -translate-x-1/2 bg-gray-900/90 text-white font-bold rounded pointer-events-none select-none leading-none"
          style={{
            top: '58%',
            fontSize: 'calc(var(--pt-card-w) / 9)',
            paddingLeft: 'calc(var(--pt-card-w) / 14)',
            paddingRight: 'calc(var(--pt-card-w) / 14)',
            paddingTop: 'calc(var(--pt-card-w) / 22)',
            paddingBottom: 'calc(var(--pt-card-w) / 22)',
          }}
        >
          Token
        </div>
      )}

      {/* Generic counter badge (bottom-left) — hidden at zero */}
      {genericCount > 0 && (
        <button
          className="absolute bottom-1 left-1 bg-gray-900/90 text-white font-bold rounded cursor-pointer select-none flex items-center justify-center hover:bg-gray-700/90 transition-colors"
          style={{
            fontSize: 'calc(var(--pt-card-w) / 7)',
            minWidth: BADGE_SIZE,
            height: BADGE_SIZE,
            lineHeight: 1,
          }}
          onClick={handleCounterClick}
          // dnd-kit's PointerSensor activates on pointerdown, so stopping
          // mousedown alone still lets a drag start from the badge.
          onPointerDown={(e) => e.stopPropagation()}
          onMouseDown={(e) => e.stopPropagation()}
          title="Generic counter — click to add 1"
          aria-label={`Generic counter: ${genericCount}. Click to add 1.`}
        >
          {genericCount}
        </button>
      )}

      {/* P/T badge (bottom-right) — shown only for creatures/vehicles and when override set */}
      {hasPT && ptDisplay && (
        <button
          className="absolute bottom-1 right-1 bg-gray-900/90 text-white font-bold rounded cursor-pointer select-none flex items-center justify-center hover:bg-gray-700/90 transition-colors"
          style={{
            fontSize: 'calc(var(--pt-card-w) / 8)',
            paddingLeft: 'calc(var(--pt-card-w) / 20)',
            paddingRight: 'calc(var(--pt-card-w) / 20)',
            height: BADGE_SIZE,
            lineHeight: 1,
          }}
          onClick={handlePTClick}
          // See above: pointerdown is what dnd-kit listens on.
          onPointerDown={(e) => e.stopPropagation()}
          onMouseDown={(e) => e.stopPropagation()}
          title="Power/Toughness — click for +1/+1"
          aria-label={`Power/Toughness: ${ptDisplay.power}/${ptDisplay.toughness}. Click for +1/+1.`}
        >
          {ptDisplay.power}/{ptDisplay.toughness}
        </button>
      )}
    </>
  );
}
